/**
 * The add-field picker (R4): "/" on the canvas, "+ Add a field" at the foot of
 * a page, "+ Field here" on a section, the Outline's "+ Add field", and the
 * phone's "+" all open this. A search where Enter adds the first match, a
 * line saying where the field will go, and the palette's tiles in their
 * groups with a one-line description each. Esc closes it (the Modal's own
 * listener); on a phone it is a sheet.
 *
 * Every tile is a `.fb-pal-item` button, the same class the sidebar's tiles
 * carry, so the plan-ceiling styling covers both.
 */
import React, { useMemo, useState } from 'react';
import { Modal } from '../../components/fs';
import PlanLimitBlock from '../../components/PlanLimitBlock';
import type { PlanLimitBody } from '../../api/limits';
import { PALETTE, PALETTE_GROUPS, PaletteEntry, searchPalette } from './builderPalette';

export default function AddFieldPicker({ where, onPick, onClose, limitHit, fieldsBlocked }: {
  /** "Adds after “Email”, in the section Your details" */
  where: React.ReactNode;
  onPick: (entry: PaletteEntry) => void;
  onClose: () => void;
  limitHit: PlanLimitBody | null;
  fieldsBlocked: boolean;
}) {
  const [q, setQ] = useState('');
  const matches = useMemo(() => searchPalette(q), [q]);
  const inert = (e: PaletteEntry) => !!e.gated && fieldsBlocked;
  const first = matches.find((e) => !inert(e));
  const grouped = q.trim()
    ? [{ group: 'Matches', items: matches }]
    : PALETTE_GROUPS.map((g) => ({ group: g, items: PALETTE.filter((e) => e.group === g) }));

  return (
    <Modal title="Add to the form" wide className="fb-picker" onClose={onClose}>
      <div className="fb-pal-sheet">
        <div className="fb-pal-search-row">
          <input
            className="fs-input fb-pal-search" placeholder="Search field types… (Enter adds the first match)"
            aria-label="Search field types" autoFocus
            value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && first) { e.preventDefault(); onPick(first); } }}
          />
          <div className="fb-pal-where">{where}</div>
        </div>
        {limitHit && <div style={{ marginBottom: 12 }}><PlanLimitBlock body={limitHit} /></div>}
        {grouped.map(({ group, items }) => (items.length ? (
          <React.Fragment key={group}>
            <div className="fb-overline">{group}</div>
            <div className="fb-pal-grid">
              {items.map((e) => (
                <button
                  key={e.label} type="button"
                  className={`fb-pal-item${e === first ? ' first' : ''}${inert(e) ? ' lim-pal-inert' : ''}`}
                  disabled={inert(e)}
                  onClick={() => onPick(e)}
                >
                  <span className="fb-pal-glyph">{e.glyph}</span>
                  <span className="fb-pal-text">
                    <span className="fb-pal-label">{e.label}</span>
                    <span className="fb-pal-desc">{e.desc}</span>
                  </span>
                </button>
              ))}
            </div>
          </React.Fragment>
        ) : null))}
        {!matches.length && <div className="fb-pal-none">Nothing called “{q}”. Try another word.</div>}
      </div>
    </Modal>
  );
}
